import React, { Fragment, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { noop } from '@lux/helpers';
import notifications from '../../config/notifications';
import validationMessages from '../../config/validation-messages.json';
import { ChooseSIMType } from '../ChooseSIMType';
import {
  TextInput,
  Text,
  Modal,
  TextLink,
  Spacing,
  Row,
  Column,
  Button,
  useDevice,
  Checkbox
} from '@dls/web';
import { FieldObject } from '../../types/registrationCheckout';
import { isValidPhoneNumber } from '../../helpers/common';
import { getRawPhoneNumber } from '../Checkout/helper';
import { trans as t } from '../../helpers/localisation';
import { ChooseSimType } from '../../constants/choose-sim-type';

interface State {
  hintMessage?: string;
  number?: string;
  termsModalOpen?: boolean;
  errorModalOpen?: boolean;
  showErrorStatus?: boolean;
}

interface PropTypes {
  errorStatus: string | boolean;
  onClick: (number: string, selectedSIMType?: string) => void;
  onModalClose: () => void;
  chooseSIMOptions: ChooseSimType[];
  selectedProduct: FieldObject;
  skipAddons?: boolean;
}

const NumberPortIn = (props: PropTypes) => {
  const { isMobile } = useDevice();

  const {
    errorStatus,
    onClick,
    onModalClose,
    chooseSIMOptions,
    selectedProduct,
    skipAddons
  } = props;

  const [state, _setState] = useState<State>({
    hintMessage: '',
    number: undefined,
    termsModalOpen: false,
    errorModalOpen: false,
    showErrorStatus: false
  });

  const setState = (stateChange: Partial<State>) => {
    _setState({ ...state, ...stateChange });
  };

  const validateInput = useCallback(
    (number: string, selectedSIMType?: string) => {
      setState({ hintMessage: '' });

      if (!isValidPhoneNumber(number)) {
        setState({
          hintMessage: validationMessages.validMobileStartingWithEight
        });
        return false;
      }

      onClick(getRawPhoneNumber(number), selectedSIMType);
    },
    [onClick]
  );

  const handleButtonClick = (selectedSIMType?: string) => {
    validateInput(state.number, selectedSIMType);
    setState({ showErrorStatus: true });
  };

  useEffect(() => {
    if (errorStatus && state.showErrorStatus) {
      setState({ errorModalOpen: true });
    }
  }, [errorStatus, state.showErrorStatus]);

  const errorMessage = errorStatus && notifications[errorStatus];

  const ErrorMessageModal = () => {
    const handleModalClose = () => {
      setState({ number: '', errorModalOpen: false, showErrorStatus: false });
      onModalClose();
    };

    return (
      errorMessage && (
        <Modal
          data-testid="error-modal"
          visible={state.errorModalOpen}
          title={errorMessage && errorMessage.title}
          onClose={handleModalClose}
        >
          <Modal.Content>
            <Spacing bottom={3} bottomMd={5}>
              <Text type="body">{errorMessage.text}</Text>
            </Spacing>
            <Button onClick={handleModalClose}>{errorMessage.ctaText}</Button>
          </Modal.Content>
        </Modal>
      )
    );
  };

  const onNumberChanged = e => {
    const number = e.target.value;
    if (isValidPhoneNumber(number)) {
      return setState({ number, hintMessage: undefined });
    }

    return setState({
      hintMessage: validationMessages.validMobileStartingWithEight,
      number
    });
  };

  return (
    <Fragment>
      <Row>
        <Column sm={12} md={12} noGutter>
          <Spacing top={isMobile ? 1 : 0.1} bottom={isMobile ? 3 : 2}>
            <Text type="header">
              {t('PORTIN_TRANSFER_MY_NUMBER_TO_SINGTEL')}
            </Text>
          </Spacing>
          <Spacing bottom={1}>
            <TextInput
              displayFormat="mobile-sg"
              bgColor={'haze'}
              label={t('YOUR_EXISTING_NUMBER')}
              value={state.number}
              maxLength={13}
              onChange={onNumberChanged}
              validation={state.hintMessage && 'danger'}
              error={!!state.hintMessage}
              hintMessage={state.hintMessage}
            />
          </Spacing>
        </Column>
      </Row>
      {isValidPhoneNumber(state.number) && (
        <ChooseSIMType
          isPortIn
          data={chooseSIMOptions}
          scrollToSimType
          transactionTypeValue="portIn"
          onSimTypeOnSelection={handleButtonClick}
          selectedProduct={selectedProduct}
          btnText={skipAddons ? t('PROCEED') : t('CHOOSE_ADD_ONS')}
        />
      )}

      {state.errorModalOpen && ErrorMessageModal()}
    </Fragment>
  );
};

NumberPortIn.defaultProps = {
  onClick: noop,
  onModalClose: noop
};

export default NumberPortIn;
